import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, OnInit, ViewChild } from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient } from '@delon/theme';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectComponent } from 'ng-zorro-antd/select';

import { BirEditModalComponent } from '../../modal/bir-edit-modal/bir-edit-modal.component';
import { BirExistModalComponent } from '../../modal/bir-exist-modal/bir-exist-modal.component';
import { BirReplaceModalComponent } from '../../modal/bir-replace-modal/bir-replace-modal.component';
import { SelectBaseComponent } from '../select-base';

@Component({
  selector: 'app-cust-bir-select',
  templateUrl: './cust-bir-select.component.html',
  styles: [
    `
      [nz-icon] {
        cursor: pointer;
      }
      .cust-bir-select-container {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .edit-button-container {
        flex-shrink: 0;
      }
      .add-new-button {
        width: 100%;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerBirSelectComponent),
      multi: true
    }
  ]
})
export class CustomerBirSelectComponent extends SelectBaseComponent implements OnInit, AfterViewInit {
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;
  @ViewChild(BirExistModalComponent) birExistModal!: BirExistModalComponent;
  @ViewChild(BirEditModalComponent) birEditModal!: BirEditModalComponent;
  @ViewChild(BirReplaceModalComponent)
  birReplaceModal!: BirReplaceModalComponent;

  NotShowInReport: boolean = false;
  showAddNewButton: boolean = true;
  storedInstantData: string = '';

  constructor(
    http: _HttpClient,
    cdr: ChangeDetectorRef,
    private formGroup: FormGroupDirective,
    private container: ControlContainer,
    private modalSrv: NzModalService,
    router: Router,
    private reuseTabService: ReuseTabService
  ) {
    super(http, cdr);
    this.valueField = '';
  }
  address: any = {};

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    
    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        this.storedInstantData = value.trim();
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.applyDefaultActiveOption(0);
      }
    });
  }

  ngAfterViewInit(): void {
    // Additional initialization if needed
    // This method is required by AfterViewInit interface
    // Initialize any view-specific logic here if needed
    this.cdr.detectChanges();
  }

  updateBIRRelatedInfo(birInfo: any) {
    const formData = this.formGroup.value;

    const birRelatedValues: any = {};
    birRelatedValues.registrationNo = birInfo.tinNumber;
    birRelatedValues.tinBranchCode = birInfo.tinBranchCode;

    if (birInfo.address) {
      const addresses = (<string>birInfo.address).split(', ');
      if (addresses.length > 0) birRelatedValues.address1 = addresses[0];
      else birRelatedValues.address1 = null;

      if (addresses.length > 1) birRelatedValues.address2 = addresses[1];
      else birRelatedValues.address2 = null;

      if (addresses.length > 2) birRelatedValues.address2 += addresses[2];
    }
    birRelatedValues.address3 = birInfo.district;
    birRelatedValues.zipCode = birInfo.zipCode;

    let newValues: any = {};
    for (let key of Object.keys(birRelatedValues)) {
      if (formData[key] !== birRelatedValues[key]) {
        newValues[key] = birRelatedValues[key];
      }
    }

    // if (Object.keys(newValues).length) {
    //   const form = this.formControl.control.parent;
    //   const modalRef = this.modalSrv.confirm({
    //     nzTitle: 'Record did not matched.',
    //     nzContent:
    //       'Do you want to replace existing TIN, Address, and Zip Code?',
    //     nzOnOk: () => {
    //       form?.patchValue(newValues);
    //       modalRef.destroy();
    //     },
    //     nzOnCancel: () => {
    //       modalRef.destroy();
    //     }
    //   });
    // }
  }

  getUrl(searchValue: string): string {
    let filter = `IsCompany ne true and IsAR eq true`;
    if (searchValue) {
      const searchFields = [
        'code',
        'companyName',
        'firstName',
        'middleName',
        'lastName',
        'tinNumber',
        'tinBranchCode',
        'address'
      ];
      const searchStr = searchValue.replaceAll("'", "''");
      const searchFilter = searchFields
        .map(x => `contains(${x}, '${encodeURIComponent(searchStr)}')`)
        .join(' or ');
      filter = `${filter} and (${searchFilter})`;
    }
    return `api/BIRClassifications/Query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
  }

  // Override search method to store instant data
  override search(value: string, maxLength: number = 10): void {
    // Store the instant data before calling parent method
    this.storedInstantData = value.trim();
    super.search(value, maxLength);
  }

  // Handle search event from template
  onSearch(value: string): void {
    this.storedInstantData = value.trim();
    this.search(value, 250);
  }

  // Override setKeyword method to store instant data
  override setKeyword(value: string, maxLength: number = 10): void {
    // Store the instant data before calling parent method
    this.storedInstantData = value.trim();
    super.setKeyword(value, maxLength);
  }

  // Handle dropdown open/close to preserve instant data and set default active option
  onDropdownOpenChange(open: boolean): void {
    if (open) {
      // run twice to cover initial render and async virtual scroll paints
      this.applyDefaultActiveOption(0);
      this.applyDefaultActiveOption(50);
    } else {
      // When dropdown closes, capture the current search input value only if we don't already have stored data
      if (!this.storedInstantData) {
        this.captureCurrentSearchValue();
      }
    }
    // Emit open change event if available
    if (this.openChanged) {
      this.openChanged(open);
    }
  }

  // Keyboard behavior: Enter selects focused item; if none focused, selects first real item
  handleKeyDown(event: KeyboardEvent): void {
    // Handle keyboard navigation for the select component
    if (event.key !== 'Enter') return;

    // Prevent default enter submitting parent forms
    event.preventDefault();
    event.stopPropagation();

    // Build visible options: skip the Add New pseudo option
    const options = (this.list || []).filter(x => x != null);
    const hasItems = options.length > 0;
    const keyword = (this.getCurrentSearchValue() || '').trim();

    // Check if the focused option is the "Add New" button
    const focusedOptionIndex = this.getFocusedOption();
    
    if (focusedOptionIndex === -1) {
      // Focus is on "Add New" button
      this.onAddNewClick();
      return;
    }

    if (hasItems) {
      // Check if there's a focused item in the dropdown
      let selectedItem;
      
      if (focusedOptionIndex >= 0 && focusedOptionIndex < options.length) {
        // Use the focused item
        selectedItem = options[focusedOptionIndex];
      } else {
        // Fallback to first item if no focused item
        selectedItem = options[0];
      }
      
      const val = this.getValue(selectedItem);
      this.selectBir(val);
      // Close dropdown
      try { this.nzSelectComponent?.setOpenState(false); } catch {}
    } else {
      // No items; if keyword present, treat as Add New
      if (keyword) {
        this.onAddNewClick();
      } else {
        try { this.nzSelectComponent?.setOpenState(false); } catch {}
      }
    }
  }

  // Method to handle add new button click
  onAddNewClick(event?: Event): void {
    // Prevent default behavior to avoid immediate focus changes
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Synchronously capture the current search value from multiple sources
    let capturedValue = '';
    
    // 1. Try to get from DOM input first (most reliable)
    capturedValue = this.captureSearchValueSync();
    
    // 2. If not found, try from search form
    if (!capturedValue) {
      capturedValue = this.getSearchFormValue();
    }
    
    // 3. If not found, try from instantData
    if (!capturedValue) {
      capturedValue = this.instantData || '';
    }
    
    if (capturedValue) {
      this.storedInstantData = capturedValue;
    }
    
    // Use setTimeout to ensure we capture the value after the current execution cycle
    setTimeout(() => {
      // Call the addNew method
      this.addNew();
    }, 0);
  }

  addNew() {
    const formData = this.formGroup.value;
    if (!formData.registrationNo || !formData.tinBranchCode) {
      this.modalSrv.warning({
        nzTitle: 'TIN is required.',
        nzContent:
          'TIN and TIN Branch Code are required before adding New BIR Info Record.'
      });
      return;
    }
    this.address = [formData.address1, formData.address2].filter(Boolean).join(', ');
    const birPayload = {
      tinNumber: formData.registrationNo,
      tinBranchCode: formData.tinBranchCode,
      isAR: true,
      address: this.address,
      district: formData.address3,
      zipCode: formData.zipCode
    };
    const skipExistCheck =
      formData.registrationNo === '000-000-000' &&
      (formData.tinBranchCode === '0000' || formData.tinBranchCode === '00000');
    if (skipExistCheck) {
      this.birEditModal.show(birPayload);
      return;
    }
    const filter = `IsCompany ne true and IsAR eq true and tinNumber eq '${formData.registrationNo}' and tinBranchCode eq '${formData.tinBranchCode}'`;

    this.http
      .get(`api/BIRClassifications/Query?_silent=true&$top=1&$filter=${filter}`)
      .subscribe(res => {
        if (res.value?.length) {
          this.birExistModal.show(res.value[0]);
        } else {
          this.birEditModal.show(birPayload);
        }
      });
  }

  selectBir(data: any) {
    if (data) {
      const newValues = this.getBirUpdateValues(data);
      if (Object.keys(newValues).length > 0) {
        this.birReplaceModal.show(data);
      }

      if (data.id) {
        if (data.isUpdate) {
          this.http.post('api/BIRClassifications/update', data).subscribe(res => {
            this.list = this.list.map(item => // update list via new value item
              item.id === res.id ? res : item
            );
            this.valueChanged(res);
          });
        } else {
          this.valueChanged(data);
        }
      } else {
        this.http.post('api/BIRClassifications/create', data).subscribe(res => {
          if (res) {
            this.list.push(res);
            this.valueChanged(res);
          }
        });
      }
    } else {
      this.valueChanged(null);
    }
  }

  replaceBir(data: any) {
    const form = <FormGroup>this.container.control;
    const newValues = this.getBirUpdateValues(data);
    form.patchValue(newValues);
  }

  editBir() {
    this.birEditModal.show(this.value);
  }

  private getBirUpdateValues(birInfo: any): any {
    const formData = this.formGroup.value;

    const birRelatedValues: any = {};
    birRelatedValues.registrationNo = birInfo.tinNumber;
    birRelatedValues.tinBranchCode = birInfo.tinBranchCode;

    if (birInfo.address) {
      const addresses = (<string>birInfo.address).split(', ');
      if (addresses.length > 0) birRelatedValues.address1 = addresses[0];
      else birRelatedValues.address1 = null;

      if (addresses.length > 1) birRelatedValues.address2 = addresses[1];
      else birRelatedValues.address2 = null;

      if (addresses.length > 2) birRelatedValues.address2 += addresses[2];
    }
    birRelatedValues.address3 = birInfo.district;
    birRelatedValues.zipCode = birInfo.zipCode;

    let newValues: any = {};
    for (let key of Object.keys(birRelatedValues)) {
      if (!formData[key] && !birRelatedValues[key]) continue;

      if (formData[key] !== birRelatedValues[key]) {
        newValues[key] = birRelatedValues[key];
      }
    }
    return newValues;
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.reuseTabService.curUrl.toLowerCase();

    if (curUrl.includes('report')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  // Method to clear stored instant data
  private clearStoredInstantData(): void {
    this.storedInstantData = '';
  }

  // Method to capture current search value
  private captureCurrentSearchValue(): void {
    if (this.nzSelectComponent) {
      const searchInput = this.nzSelectComponent.originElement.nativeElement.querySelector('input');
      if (searchInput && searchInput.value) {
        this.storedInstantData = searchInput.value.trim();
      }
    }
  }

  // Method to get current search value from DOM
  private getCurrentSearchValue(): string {
    if (this.nzSelectComponent) {
      const searchInput = this.nzSelectComponent.originElement.nativeElement.querySelector('input');
      if (searchInput && searchInput.value) {
        return searchInput.value.trim();
      }
    }
    return this.storedInstantData || '';
  }

  // Method to capture current search value synchronously
  private captureSearchValueSync(): string {
    if (this.nzSelectComponent) {
      const searchInput = this.nzSelectComponent.originElement.nativeElement.querySelector('input');
      if (searchInput && searchInput.value) {
        return searchInput.value.trim();
      }
    }
    return this.instantData || '';
  }

  // Method to get current search value from search form
  private getSearchFormValue(): string {
    const formValue = this.searchForm.controls.keyword.value;
    return formValue ? formValue.trim() : '';
  }

  // Force correct default active option: first real item (not add new button)
  private applyDefaultActiveOption(delayMs: number = 0): void {
    setTimeout(() => {
      try {
        const dropdowns = document.querySelectorAll('.dropdownSelect');
        const dropdown = dropdowns[dropdowns.length - 1] as HTMLElement | undefined;
        if (!dropdown) return;
        const options = dropdown.querySelectorAll('.ant-select-item-option');
        if (!options || options.length === 0) return;
        
        // Calculate the number of special options (Add New button)
        const totalSpecialOptions = 1; // Only Add New button
        
        // Determine target index: 
        // - If more than one option exists, highlight the first real result (index 1)
        // - Otherwise highlight the first option (index 0)
        let targetIndex = 0;
        if (!this.NotShowInReport && options.length > 1) {
          targetIndex = 1; // First real result (skip Add New button)
        }
        
        // Use a more robust approach: simulate mouse events to properly manage state
        this.setActiveOptionByIndex(options, targetIndex);
      } catch {}
    }, delayMs);
  }

  /**
   * Sets the active option by index using proper event simulation
   */
  private setActiveOptionByIndex(options: NodeListOf<Element>, targetIndex: number): void {
    // First, clear all active states by simulating mouse leave on all options
    options.forEach((opt, index) => {
      if (index !== targetIndex) {
        const mouseLeaveEvent = new MouseEvent('mouseleave', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        opt.dispatchEvent(mouseLeaveEvent);
      }
    });
    
    // Then simulate mouse enter on the target option
    if (options[targetIndex]) {
      const mouseEnterEvent = new MouseEvent('mouseenter', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      options[targetIndex].dispatchEvent(mouseEnterEvent);
    }
  }

  // Helper method to get the currently focused option index
  private getFocusedOption(): number {
    try {
      // Try to get the focused index from nzSelectComponent properties
      const nzSelectComponent = this.nzSelectComponent as any;

      if (nzSelectComponent) {            
        // Use activatedValue to find the correct index in our Customer BIR list
        if (nzSelectComponent.activatedValue) {
          const activatedBir = nzSelectComponent.activatedValue;        
          
          // Find this Customer BIR in our list
          const actualIndex = this.list.findIndex(bir => {
            // Try different ways to match the Customer BIR
            return bir.id === activatedBir.id || 
                  bir.code === activatedBir.code ||
                  bir.id === activatedBir ||
                  bir.code === activatedBir;
          });   
          
          if (actualIndex >= 0) {
            return actualIndex;
          }
        }                
      }

      const dropdowns = document.querySelectorAll('.dropdownSelect');
      const dropdown = dropdowns[dropdowns.length - 1] as HTMLElement | undefined;
      if (!dropdown) {
        return -1;
      }
      
      const options = dropdown.querySelectorAll('.ant-select-item-option');
      
      for (let i = 0; i < options.length; i++) {
        const option = options[i] as HTMLElement;
        if (option.classList.contains('ant-select-item-option-active')) {
          // If it's the first option (Add New), return -1 to indicate Add New is focused
          if (i === 0) {
            return -1; // -1 indicates Add New button is focused
          }
          
          // Convert to actual list index (accounting for Add New option)
          let actualIndex = i - 1; // Start with Add New offset
          
          return actualIndex;
        }
      }
      
    } catch (error) {
      console.error('Error in getFocusedOption:', error);
    }
    return -1;
  }
}
